import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { formatNumber } from "@/lib/helper";

export type MarketRateProposal = {
  id: string;
  currency: string;
  buyingOffset: number;
  buyingRate: number | "loading" | null;
  sellingOffset: number;
  sellingRate: number | "loading" | null;
  /** Buy-side offset type (FLAT | MANUAL | PERCENTAGE) */
  buyType: "FLAT" | "MANUAL" | "PERCENTAGE";
  /** Sell-side offset type */
  sellType: "FLAT" | "MANUAL" | "PERCENTAGE";
  /** Same as buyType — kept for edit modal / legacy filter */
  type: "FLAT" | "MANUAL" | "PERCENTAGE";
  /** Raw buy rate from supported pair (provider feed) */
  buyRate?: number | string | null;
  /** Raw sell rate from supported pair */
  sellRate?: number | string | null;
  buyRateProvider?: string | null;
  sellRateProvider?: string | null;
  supportedPairId: string;
};

function formatTableRate(rate: number | string | null | undefined): string {
  if (rate === undefined || rate === null || rate === "") return "—";
  const num = typeof rate === "string" ? parseFloat(rate) : rate;
  if (Number.isNaN(num)) return "—";
  const abs = Math.abs(num);
  const decimals = abs >= 1_000 ? 2 : abs >= 1 ? 4 : 8;
  return formatNumber(num, decimals);
}

function formatProvider(provider: string | null | undefined): string {
  if (!provider?.trim()) return "—";
  const p = provider.trim();
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
}

function formatEffectiveRate(
  value: number | "loading" | null | undefined
): string {
  if (value === "loading" || value === null || value === undefined) {
    return "—";
  }
  return formatTableRate(value);
}

const groupHeaderClass =
  "text-center font-semibold text-primary-3 bg-primary-0/40 border-x border-border/60 py-2";

export const getColumns = (handlers: {
  onEdit: (row: MarketRateProposal) => void;
  onDelete: (row: MarketRateProposal) => void;
}): ColumnDef<MarketRateProposal>[] => [
  {
    accessorKey: "currency",
    header: () => <div className="font-medium">CURRENCY PAIR</div>,
    cell: ({ row }) => (
      <div className="text-left font-medium">{row.getValue("currency")}</div>
    ),
  },
  {
    id: "buySide",
    header: () => <div className={groupHeaderClass}>Buy</div>,
    columns: [
      {
        accessorKey: "buyRateProvider",
        header: () => (
          <div className="text-center text-xs font-medium">BUY PROVIDER/RATE</div>
        ),
        cell: ({ row }) => (
          <div className="text-center text-sm">
            {formatProvider(row.original.buyRateProvider)} / {formatTableRate(row.original.buyRate)}
          </div>
        ),
      },
      {
        accessorKey: "buyType",
        header: () => <div className="text-center text-xs font-medium">BUY OFFSET</div>,
        cell: ({ row }) => (
          <div className="text-center">{row.original.buyType}</div>
        ),
      },
      {
        accessorKey: "buyingOffset",
        header: () => (
          <div className="text-center text-xs font-medium">OFFSET VALUE</div>
        ),
        cell: ({ row }) => (
          <div className="text-center text-sm">{row.original.buyingOffset}</div>
        ),
      },
      // {
      //   accessorKey: "buyingRate",
      //   header: () => (
      //     <div className="text-center text-xs font-medium">EFFECTIVE RATE</div>
      //   ),
      //   cell: ({ row }) => (
      //     <div className="text-right text-sm font-medium">
      //       {formatEffectiveRate(row.original.buyingRate)}
      //     </div>
      //   ),
      // },
    ],
  },
  {
    id: "sellSide",
    header: () => <div className={groupHeaderClass}>Sell</div>,
    columns: [
      {
        accessorKey: "sellRateProvider",
        header: () => (
          <div className="text-center text-xs font-medium">SELL PROVIDER/RATE</div>
        ),
        cell: ({ row }) => (
          <div className="text-center text-sm">
            {formatProvider(row.original.sellRateProvider)} / {formatTableRate(row.original.sellRate)}
          </div>
        ),
      },
      {
        accessorKey: "sellType",
        header: () => <div className="text-center text-xs font-medium">SELL OFFSET</div>,
        cell: ({ row }) => (
          <div className="text-center">{row.original.sellType}</div>
        ),
      },
      
      // {
      //   accessorKey: "sellRate",
      //   header: () => (
      //     <div className="text-center text-xs font-medium">PAIR RATE</div>
      //   ),
      //   cell: ({ row }) => (
      //     <div className="text-right text-sm">
      //       {formatTableRate(row.original.sellRate)}
      //     </div>
      //   ),
      // },
      {
        accessorKey: "sellingOffset",
        header: () => (
          <div className="text-center text-xs font-medium">OFFSET VALUE</div>
        ),
        cell: ({ row }) => (
          <div className="text-center text-sm">{row.original.sellingOffset}</div>
        ),
      },
      // {
      //   accessorKey: "sellingRate",
      //   header: () => (
      //     <div className="text-center text-xs font-medium">EFFECTIVE RATE</div>
      //   ),
      //   cell: ({ row }) => (
      //     <div className="text-right text-sm font-medium">
      //       {formatEffectiveRate(row.original.sellingRate)}
      //     </div>
      //   ),
      // },
    ],
  },
  {
    id: "actions",
    header: () => null,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-primary-9 hover:text-primary"
          onClick={() => handlers.onEdit(row.original)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-primary-9 hover:text-error"
          onClick={() => handlers.onDelete(row.original)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ),
  },
];
